import React from 'react';
import { MonoTag, DoubleRule } from '../shared';

export interface TopPerformer {
  rank: string;
  pair: string;
  exch: string;
}

export interface CompleteSummary {
  email: string;
  exchangeName: string;
  emailsEnabled: boolean;
}

const TopBots: React.FC<{
  performers: TopPerformer[];
  tabletRail?: boolean;
}> = ({ performers, tabletRail }) => (
  <div
    style={{
      border: '1px solid #383838',
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      background: '#080808',
    }}
  >
    <MonoTag color="#00B061">{'// top performers'}</MonoTag>
    <div
      style={{ fontSize: 13, color: '#9a9a9a', lineHeight: 1.5, marginTop: -4 }}
    >
      Best performing bots at the moment — a place to start once the dashboard
      opens.
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', marginTop: 4 }}>
      {performers.length === 0 && (
        <div
          style={{
            fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
            fontSize: 12,
            color: '#787878',
            padding: '16px 0',
            borderTop: '1px solid #383838',
            letterSpacing: '.06em',
          }}
        >
          Loading…
        </div>
      )}
      {performers.map((b, i) => (
        <div
          key={b.rank}
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: 14,
            alignItems: 'center',
            padding: '16px 0',
            borderTop: i === 0 ? '1px solid #383838' : '1px solid #2c2c2c',
          }}
        >
          <MonoTag color="#787878" size={11}>
            {b.rank}
          </MonoTag>
          <div
            style={{
              fontSize: tabletRail ? 13 : 15,
              color: '#fff',
              fontWeight: 500,
            }}
          >
            {b.pair}
          </div>
          <div
            style={{
              fontFamily: 'Bebas Neue, Kanit',
              fontSize: tabletRail ? 15 : 18,
              color: '#9a9a9a',
              letterSpacing: '.06em',
            }}
          >
            {b.exch}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Complete: React.FC<{
  summary: CompleteSummary;
  performers: TopPerformer[];
  phone?: boolean;
  narrow?: boolean;
  tabletRail?: boolean;
}> = ({ summary, performers, phone, narrow, tabletRail }) => {
  const rows: [string, string][] = [
    ['Account', summary.email || 'wouter.120@hotmail.com'],
    ['Exchange', `${summary.exchangeName} · connected`],
    ['Credits', '50 / 50 available'],
    ['Emails', summary.emailsEnabled ? 'Enabled (transactional)' : 'Disabled'],
  ];
  return (
    <div
      style={{
        padding: phone ? '28px 20px 20px' : '48px 56px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        minHeight: 0,
      }}
    >
      <div>
        <h1
          style={{
            margin: '0 0 12px 0',
            fontSize: phone ? 38 : 64,
            fontWeight: 600,
            lineHeight: 0.95,
            letterSpacing: '-.01em',
          }}
        >
          All systems <span style={{ color: '#00B061' }}>armed.</span>
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: 15,
            color: '#9a9a9a',
            maxWidth: 580,
            lineHeight: 1.6,
          }}
        >
          That marks the end of the onboarding process. You&apos;re now ready to
          subscribe to your first bot. Remember: still real money in risky
          investments — proceed with caution.
        </p>
      </div>
      <DoubleRule />
      <div
        className="no-scrollbar"
        style={{
          flex: 1,
          display: 'flex',
          alignItems: narrow ? 'flex-start' : 'center',
          minHeight: 0,
          overflowY: phone ? 'visible' : 'auto',
          scrollbarWidth: 'none',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: narrow ? '1fr' : '1.1875fr 1.0625fr',
            gap: narrow ? 28 : 56,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <MonoTag color="#787878">{'// session summary'}</MonoTag>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0,
                marginTop: 4,
              }}
            >
              {rows.map(([k, v], i) => (
                <div
                  key={k}
                  style={{
                    padding: '14px 0',
                    borderTop: '1px solid #383838',
                    borderRight: i % 2 === 0 ? '1px solid #383838' : 'none',
                    paddingRight: i % 2 === 0 ? 28 : 0,
                    paddingLeft: i % 2 === 0 ? 0 : 28,
                  }}
                >
                  <MonoTag color="#787878" size={10}>
                    {k}
                  </MonoTag>
                  <div style={{ marginTop: 6, fontSize: 15, color: '#fff' }}>
                    {v}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 'auto',
                paddingTop: 20,
                borderTop: '1px solid #383838',
              }}
            >
              <MonoTag color="#787878">
                next surface · overview → discover
              </MonoTag>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 14,
                  color: '#c4c1c1',
                  lineHeight: 1.6,
                  maxWidth: 460,
                }}
              >
                The dashboard opens on the Overview. Head to Discover to
                subscribe to a bot — the leaderboard on the right is a good
                place to start.
              </div>
            </div>
          </div>
          <TopBots performers={performers} tabletRail={tabletRail} />
        </div>
      </div>
    </div>
  );
};

export default Complete;
